/**
 * Formula evaluation utility for catalog pricing
 * Evaluates formula strings with given variables using safe evaluation
 */

import { Parser } from 'expr-eval'

/**
 * Evaluates a formula string with given variables
 * Uses expr-eval library for safe evaluation (no eval or Function constructor)
 * @param {string} formula - The formula string to evaluate
 * @param {Object} variables - Object containing variable values
 * @returns {number|null} - The calculated result or null if evaluation fails
 */
export function evaluateFormula(formula, variables) {
  if (!formula || typeof formula !== 'string') return null

  try {
    // Create a parser instance
    const parser = new Parser()

    // Parse the formula (case-insensitive by converting to lowercase)
    const expression = parser.parse(formula.toLowerCase())

    // Prepare variables with default values
    const safeVariables = {}
    for (const key in variables) {
      safeVariables[key.toLowerCase()] = variables[key] || 0
    }

    // Evaluate with the provided variables
    const result = expression.evaluate(safeVariables)

    return isFinite(result) ? Math.round(result * 100) / 100 : null
  } catch (e) {
    console.error('Formula evaluation error:', e)
    return null
  }
}

/**
 * Gets formula variables from a listing task's extra_data
 * @param {Object} listingTask - The listing task object
 * @returns {Object} - Object containing all available formula variables
 */
export function getFormulaVariables(listingTask) {
  const extraData = listingTask.extra_data || {}
  const reservationPricing =
    extraData.reservation_pricing || listingTask.reservation_pricing || {}
  const listingPricing =
    extraData.listing_pricing || listingTask.listing_pricing || {}

  return {
    adr: reservationPricing.adr || 0,
    nights_count: reservationPricing.nights_count || 0,
    fare_accommodation: reservationPricing.fare_accommodation || 0,
    guests_count: reservationPricing.guests_count || 0,
    cleaning_fee: reservationPricing.cleaning_fee || 0,
    price: reservationPricing.price || 0,
    beds: listingPricing.beds || 0,
    bedrooms: listingPricing.bedrooms || 0,
  }
}

/**
 * Calculates the formula-based price for a listing task
 * @param {Object} listingTask - The listing task object
 * @returns {number|null} - The calculated price or null if not applicable
 */
export function calculateCatalogFormulaPrice(listingTask) {
  const catalog = listingTask.listing_task_catalog_item
  if (
    !catalog ||
    catalog.pricing_type !== 'formula' ||
    !catalog.price_formula
  ) {
    return null
  }

  const variables = getFormulaVariables(listingTask)
  return evaluateFormula(catalog.price_formula, variables)
}
